<script lang="ts">
  import { keyboard } from '../keys/keyboard.svelte';
  import type { Snippet } from 'svelte';
  import type { Block, Row, Scope } from '../engine/types';
  import type { ScopeFold } from '../logic/scopeFold';
  import type { FileDiff } from '../state/diffs.svelte';
  import { renderLine } from '../logic/render';
  import { blockLines, type Cell, type Coverage, type Stub, type VisualLine } from '../logic/partial';
  import type { LineCursor, LineSelection, MoveLink } from '../state/anchor';
  import PendingTag from './PendingTag.svelte';
  import ScopePath from './ScopePath.svelte';
  import ComplexityMark from './ComplexityMark.svelte';
  import { effectiveComplexity } from '../logic/complexity';
  import { blockScope } from '../logic/scope';

  interface Props {
    block: Block;
    ordinal: number | null;
    diff: FileDiff;
    mode: 'split' | 'unified';
    reviewed: boolean;
    /** Which rows the review marks cover; null until known. */
    coverage: Coverage | null;
    /** The block's displayed lines in `mode` (`visualLines`). */
    lines: VisualLine[];
    /** Covered runs folded into "✓ N lines reviewed" (`coveredStubs`), and whether each is open. */
    stubs: Stub[];
    stubOpen: (stub: Stub) => boolean;
    onstub: (stub: Stub, open: boolean) => void;
    /** Displayed lines a folded scope hides, and the folds whose one line shows here (before line `at`). */
    scopeHidden?: ReadonlySet<number>;
    /** The collapsed scope folds shown in this block, each before line `at`, with the review of the changed lines it hides (null when unknown). */
    scopeStubs?: { at: number; fold: ScopeFold; review?: { done: number; total: number; changes: number } | null }[];
    /** The foldable scope whose head is new-side line `n`, and whether it is folded; null for none. */
    scopeHead?: (n: number) => { scope: Scope; folded: boolean } | null;
    /** A scope's ▾ was clicked (Alt-click: `select`), or a folded scope's line. */
    onscope?: (scope: Scope, select: boolean) => void;
    /** The chip on a definition's last line was clicked: go to its head. */
    onscopego?: (side: 'LEFT' | 'RIGHT', scope: Scope) => void;
    /** That chip is hovered or focused (with it), or no longer is (null): peek at the definition. */
    onscopepeek?: (peek: { side: 'LEFT' | 'RIGHT'; scope: Scope; el: HTMLElement } | null) => void;
    folded: boolean;
    active: boolean;
    selection: LineSelection | null;
    /** The keyboard line cursor, when it is in this file. */
    lineCursor?: LineCursor | null;
    /** Review marks can be stored (false while the diff engine is not loaded). */
    canReview?: boolean;
    /** Why review marks are off, as the checkbox's tooltip (with `canReview` false). */
    reviewOffText?: string;
    /** The block's review mark is not yet saved on the server. */
    saving?: boolean;
    /** Unresolved threads, the viewer's drafts and their private notes in the block: shown on its head, so a folded block says so. */
    badges: { threads: number; drafts: number; notes: number };
    moveAt: (row: Row) => MoveLink | null;
    hasInline: (anchors: string[]) => boolean;
    inline: Snippet<[string[], string]>;
    onfold: () => void;
    onreview: (value: boolean) => void;
    onselect: () => void;
    ongutter: (e: MouseEvent, side: 'LEFT' | 'RIGHT', line: number) => void;
    ongutterpointer: (e: PointerEvent) => void;
  }
  let {
    block,
    ordinal,
    diff,
    mode,
    reviewed,
    coverage,
    lines,
    stubs,
    stubOpen,
    onstub,
    scopeHidden = new Set(),
    scopeStubs = [],
    scopeHead = () => null,
    onscope = () => {},
    onscopego,
    onscopepeek,
    folded,
    active,
    selection,
    lineCursor = null,
    canReview = true,
    reviewOffText = 'Review marks are unavailable',
    saving = false,
    badges,
    moveAt,
    hasInline,
    inline,
    onfold,
    onreview,
    onselect,
    ongutter,
    ongutterpointer,
  }: Props = $props();
  /** The key that does what the Reviewed checkbox would. */
  /** The key that marks or unmarks this change: shown on the cursor's change alone, the one the key acts on. */
  const markKey = $derived(active ? keyboard.key({ kind: 'act', verb: reviewed ? 'unmark' : 'review' }) : null);
  const foldHint = $derived(keyboard.hint({ kind: 'fold', to: 'toggle' }));
  const markTitle = $derived.by(() => {
    const on = keyboard.key({ kind: 'act', verb: 'review' });
    const off = keyboard.key({ kind: 'act', verb: 'unmark' });
    const onward = keyboard.key({ kind: 'act', verb: 'review', advance: true });
    return `Toggle reviewed${on && off ? ` (mark: ${on}, unmark: ${off})` : ''}.${onward ? ` Mark reviewed and go to the next unreviewed change: ${onward}.` : ''}`;
  });

  /** The lines to draw, with each folded stub in place of its lines and an open one's header before them. */
  type Part =
    | { kind: 'line'; line: VisualLine; i: number }
    | { kind: 'stub'; stub: Stub; open: boolean }
    | { kind: 'scope'; fold: ScopeFold; review: { done: number; total: number; changes: number } | null };
  const parts: Part[] = $derived.by(() => {
    const out: Part[] = [];
    const scopeAt = new Map(scopeStubs.map((s) => [s.at, s.fold]));
    const reviewAt = new Map(scopeStubs.map((s) => [s.fold.id, s.review ?? null]));
    const line = (i: number) => {
      const fold = scopeAt.get(i);
      if (fold) out.push({ kind: 'scope', fold, review: reviewAt.get(fold.id) ?? null });
      if (!scopeHidden.has(i)) out.push({ kind: 'line', line: lines[i], i });
    };
    let i = 0;
    for (const stub of stubs) {
      for (; i < stub.from; i++) line(i);
      const open = stubOpen(stub);
      // A reviewed run all inside a folded scope folds with it.
      let shown = false;
      for (let j = stub.from; j < stub.to; j++) if (!scopeHidden.has(j)) shown = true;
      if (shown) out.push({ kind: 'stub', stub, open });
      if (open) for (; i < stub.to; i++) line(i);
      i = stub.to;
    }
    for (; i < lines.length; i++) line(i);
    return out;
  });

  /** The definitions the change sits in, once its side is highlighted; the engine's title until then, or when it sits in none. */
  const scope = $derived(block.kind === 'change' ? blockScope(block, diff.oldScopes, diff.newScopes) : null);
  const scopeLabel = $derived(scope?.length ? scope.map((s) => s.title).join(' › ') + (scope.some((s) => s.test) ? ' (test code)' : '') : block.title);
  /** How much the change asks of a reviewer, its moved rows discounted; null for context and unscored blocks. */
  const complexity = $derived(block.kind === 'change' ? effectiveComplexity(block, (r) => !!moveAt(r)) : null);

  const progress = $derived(block.kind === 'change' ? blockLines(block, coverage) : null);
  const partial = $derived(!!progress && progress.done > 0 && progress.done < progress.total);

  function html(cell: NonNullable<Cell>): string {
    const { row, side } = cell;
    const n = side === 'old' ? row.old! : row.new!;
    const text = (side === 'old' ? diff.oldLines : diff.newLines)[n - 1] ?? '';
    const hl = (side === 'old' ? diff.oldHl : diff.newHl)?.[n - 1] ?? [];
    const emph = (side === 'old' && row.kind === 'del') || (side === 'new' && row.kind === 'add') ? row.emphasis : [];
    return renderLine(text, hl, emph);
  }

  function selected(side: 'LEFT' | 'RIGHT', n: number): boolean {
    if (!selection || selection.side !== side) return false;
    const lo = Math.min(selection.start, selection.end);
    const hi = Math.max(selection.start, selection.end);
    return n >= lo && n <= hi;
  }

  /** The fewest lines a definition spans for its last line to name it: a short one's head is in sight. */
  const END_CHIP_MIN = 6;
  /** Each side's definitions by their last line, the innermost where several end on one. */
  function endsOf(scopes: readonly Scope[] | null): Map<number, Scope> {
    const out = new Map<number, Scope>();
    for (const sc of scopes ?? []) if (sc.endLine - sc.headLine + 1 >= END_CHIP_MIN) out.set(sc.endLine, sc);
    return out;
  }
  const ends = $derived(onscopego ? { LEFT: endsOf(diff.oldScopes), RIGHT: endsOf(diff.newScopes) } : null);

  const contextLabel = $derived(`Unchanged · ${block.rows.length} line${block.rows.length === 1 ? '' : 's'}`);
</script>

{#snippet cell(c: Cell)}
  {#if !c}
    <div class="source-cell vacant" aria-hidden="true"><span class="gutter"></span><code></code></div>
  {:else}
    {@const n = c.side === 'old' ? c.row.old! : c.row.new!}
    {@const side = c.side === 'old' ? 'LEFT' : 'RIGHT'}
    {@const kind = c.row.kind === 'del' ? 'removed' : c.row.kind === 'add' ? 'added' : ''}
    {@const mv = c.row.kind !== 'context' ? moveAt(c.row) : null}
    <div
      class="source-cell {kind}"
      class:covered={!!coverage?.covered[c.index]}
      class:selected={selected(side, n)}
      class:line-cursor={!!lineCursor && lineCursor.side === side && lineCursor.line === n}
      class:moved={!!mv}
      data-side={side}
      data-line={n}
    >
      {#if c.side === 'new'}
        {@const head = scopeHead(n)}
        {#if head}
          <button
            class="scope-chevron"
            class:folded={head.folded}
            aria-expanded={!head.folded}
            aria-label="{head.folded ? 'Unfold' : 'Fold'} {head.scope.title}"
            title="{head.folded ? 'Unfold' : 'Fold'} {head.scope.title}{foldHint}. Alt-click selects it, to comment on or mark reviewed"
            onclick={(e) => onscope(head.scope, e.altKey)}>{head.folded ? '▸' : '▾'}</button
          >
        {/if}
      {/if}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <span
        class="gutter"
        title="Comment on {c.side} line {n}. Drag or shift-click for a range to comment on or mark reviewed"
        onmousedown={(e) => ongutter(e, side, n)}
        onpointerdown={ongutterpointer}>{n}</span
      ><span class="change-sign">{mv ? '⇄' : kind === 'removed' ? '−' : kind === 'added' ? '+' : ''}</span><code
        >{@html html(c)}</code
      >{#if mv}<button class="move-link" title={mv.title} onclick={mv.go}>{mv.label}</button>{/if}{#if ends}{@const closes = ends[side].get(n)}{#if closes}<button
            class="scope-end"
            title="End of {closes.title}: go to line {closes.headLine}"
            onclick={() => onscopego?.(side, closes)}
            onpointerenter={(e) => onscopepeek?.({ side, scope: closes, el: e.currentTarget })}
            onpointerleave={() => onscopepeek?.(null)}
            onfocus={(e) => onscopepeek?.({ side, scope: closes, el: e.currentTarget })}
            onblur={() => onscopepeek?.(null)}>↑ {closes.title}</button
          >{/if}{/if}
    </div>
  {/if}
{/snippet}

<section
  class="diff-block {block.kind}"
  class:reviewed
  class:cursor-active={active}
  aria-current={active ? 'true' : undefined}
  data-block={block.key}
>
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div
    class="block-head"
    onclick={(e) => {
      if (!(e.target as Element).closest('button,input,label,a')) onselect();
    }}
    ondblclick={(e) => {
      if (!(e.target as Element).closest('button,input,label,a')) onfold();
    }}
  >
    <button class="fold-button" aria-expanded={!folded} title="Fold or unfold this block" onclick={onfold}>{folded ? '▸' : '▾'}</button>
    {#if block.kind === 'change'}
      <span class="block-type">CHANGE {ordinal}</span>
      <span class="block-title" title={scopeLabel}>{#if scope?.length}<ScopePath scopes={scope} />{:else}{block.title}{/if}</span>
      <span class="block-delta"><span class="positive">+{block.added}</span> <span class="negative">−{block.removed}</span></span>
      {#if complexity}<ComplexityMark {complexity} id="cx-{block.key}" />{/if}
      <span class="block-badges">
        {#if badges.threads}<span class="badge thread" title="Unresolved threads">● {badges.threads}</span>{/if}
        {#if badges.drafts}<span class="badge draft" title="Your drafts">✎ {badges.drafts}</span>{/if}
        {#if badges.notes}<span class="badge note" title="Your private notes: only you see them">◆ {badges.notes}</span>{/if}
      </span>
      <!-- Progress, the saving tag and the toggle stay together at the right, whatever shows. -->
      <span class="block-status">
        {#if partial && progress}
          <span class="block-progress" id="progress-{block.key}" aria-label="{progress.done} of {progress.total} changed lines reviewed" title="{progress.done} of {progress.total} changed lines reviewed">◐ {progress.done}/{progress.total}</span>
        {/if}
        {#if saving}<PendingTag label="Saving" />{/if}
        <label
          class="review-toggle"
          class:unavailable={!canReview}
          class:is-pending={saving}
          aria-busy={saving ? 'true' : undefined}
          title={canReview ? markTitle : reviewOffText}
        >
          <input
            type="checkbox"
            checked={reviewed}
            indeterminate={partial}
            aria-describedby={partial ? `progress-${block.key}` : undefined}
            disabled={!canReview}
            onchange={(e) => onreview((e.currentTarget as HTMLInputElement).checked)}
          />
          <span>Reviewed {#if markKey}<kbd>{markKey}</kbd>{/if}</span>
        </label>
      </span>
    {:else}
      <span class="context-label">{contextLabel}</span>
      {#if badges.threads || badges.drafts || badges.notes}
        <span class="block-badges">
          {#if badges.threads}<span class="badge thread">● {badges.threads}</span>{/if}
          {#if badges.drafts}<span class="badge draft">✎ {badges.drafts}</span>{/if}
          {#if badges.notes}<span class="badge note" title="Your private notes: only you see them">◆ {badges.notes}</span>{/if}
        </span>
      {/if}
    {/if}
  </div>
  {#if !folded}
    <div class="block-lines" class:split={mode === 'split'}>
      {#each parts as part (part.kind === 'line' ? part.i : part.kind === 'stub' ? `stub:${part.stub.from}` : `scope:${part.fold.id}`)}
        {#if part.kind === 'scope'}
          {@const f = part.fold}
          {@const rv = part.review?.total ? part.review : null}
          <button
            class="scope-stub"
            class:reviewed={!!rv && rv.done === rv.total}
            class:partial={!!rv && rv.done > 0 && rv.done < rv.total}
            aria-expanded="false"
            title="Unfold {f.scope.title}{foldHint}{rv ? `. ${rv.done} of ${rv.total} changed lines in it reviewed; a review verb on the line above acts on all of them` : ''}"
            onclick={() => onscope(f.scope, false)}
            ><span class="stub-chevron">▸</span> … {f.lines} line{f.lines === 1 ? '' : 's'}{#if f.added || f.removed}&ensp;<span class="positive">+{f.added}</span>
              <span class="negative">−{f.removed}</span>{/if}{#if rv}<span class="scope-stub-review"
                >{rv.done === rv.total ? '✓ reviewed' : rv.done ? `◐ ${rv.done}/${rv.total} reviewed` : `○ 0/${rv.total} reviewed`}</span
              >{/if}<span class="scope-stub-title">{f.scope.title}</span></button
          >
        {:else if part.kind === 'stub'}
          <button
            class="reviewed-stub"
            class:open={part.open}
            aria-expanded={part.open}
            title={part.open ? 'Fold these reviewed lines' : 'Show these reviewed lines'}
            onclick={() => onstub(part.stub, !part.open)}
            ><span class="stub-chevron">{part.open ? '▾' : '▸'}</span> ✓ {part.stub.lines} line{part.stub.lines === 1 ? '' : 's'} reviewed</button
          >
        {:else}
          {@const line = part.line}
          {#if mode === 'unified'}
            <div class="unified-row">{@render cell(line.left)}</div>
          {:else}
            <div class="split-row">{@render cell(line.left)}{@render cell(line.right)}</div>
          {/if}
          {#if hasInline(line.anchors)}
            <div class="inline">{@render inline(line.anchors, block.key)}</div>
          {/if}
        {/if}
      {/each}
    </div>
  {/if}
</section>
